import { memo, type CSSProperties } from 'react';
import { AlertTriangle, ChevronDown, Sparkles } from 'lucide-react';
import type { EpcRoleTag } from './RoleEditor/types';
import { SOVEREIGN } from '../../epc/utils/epcProjectVisual';

/**
 * Auto-derived permissions card for the EPC Projects module.
 *
 * The classic 4-tier picker used to live here; it duplicated the "Acts as"
 * tier the admin already picked on Step 2 (DepartmentSection). This card
 * reads that `tag` value, lists the actions auto-granted for the tier, and
 * exposes an Advanced disclosure so power users can override the raw
 * CRUD checkboxes.
 *
 * Source of truth for the mapping: TIER_AUTO_GRANTS (this file). The
 * provider mirrors that map on every tag change to keep
 * formValues.permissions.epcProject in sync.
 */

export const TIER_AUTO_GRANTS: Record<
  EpcRoleTag,
  { action: string; label: string }[]
> = {
  epc_head: [
    { action: 'read', label: 'Read' },
    { action: 'update', label: 'Update' },
    { action: 'import', label: 'Import' },
    { action: 'projectHead', label: 'Project-head workspace' },
    { action: 'departmentHead', label: 'Dept-head workspace' },
    { action: 'departmentManager', label: 'Dept-manager workspace' },
  ],
  pmo: [
    { action: 'read', label: 'Read' },
    { action: 'update', label: 'Update' },
    { action: 'projectHead', label: 'Project-head workspace' },
    { action: 'departmentHead', label: 'Dept-head workspace' },
    { action: 'departmentManager', label: 'Dept-manager workspace' },
  ],
  project_head: [
    { action: 'read', label: 'Read' },
    { action: 'projectHead', label: 'Project-head workspace' },
  ],
  project_coordinator: [{ action: 'read', label: 'Read' }],
  department_head: [
    { action: 'read', label: 'Read' },
    { action: 'departmentHead', label: 'Dept-head workspace' },
  ],
  manager: [
    { action: 'read', label: 'Read' },
    { action: 'departmentManager', label: 'Dept-manager workspace' },
  ],
  site_engineer: [{ action: 'read', label: 'Read' }],
};

const TIER_LABELS: Record<EpcRoleTag, string> = {
  epc_head: 'EPC Head',
  pmo: 'PMO',
  project_head: 'Project Head',
  project_coordinator: 'Project Coordinator',
  department_head: 'Department Head',
  manager: 'Manager',
  site_engineer: 'Site Engineer',
};

const CRUD_ACTIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'create', label: 'Create' },
  { key: 'read', label: 'Read' },
  { key: 'update', label: 'Update' },
  { key: 'delete', label: 'Delete' },
  { key: 'import', label: 'Import' },
];

const TIER_ACTIONS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'projectHead', label: 'Project-head workspace' },
  { key: 'departmentHead', label: 'Dept-head workspace' },
  { key: 'departmentManager', label: 'Dept-manager workspace' },
];

export interface EpcProjectsTierAutoGrantProps {
  /** Current EPC role tier selected on Step 2 (Department). Undefined when not yet picked. */
  tag: EpcRoleTag | undefined;
  /** Granted action keys for the epcProject module — drives the Advanced disclosure checkboxes. */
  granted: Set<string>;
  /** Toggle a single (epcProject, action) cell — same signature as PermissionMatrix.handleCellToggle. */
  onTogglePermission: (action: string, next: boolean) => void;
  /** Optional jump-back to Step 2 — keeps the "Change" link in the banner functional. */
  onJumpToDepartmentStep?: () => void;
}

function EpcProjectsTierAutoGrantImpl({
  tag,
  granted,
  onTogglePermission,
  onJumpToDepartmentStep,
}: EpcProjectsTierAutoGrantProps) {
  if (!tag) {
    return (
      <section
        className="rounded-2xl p-5"
        style={{
          border: `2px solid ${SOVEREIGN.champagne}`,
          background: 'rgba(201,169,97,0.07)',
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
            style={{ background: 'rgba(201,169,97,0.22)', color: SOVEREIGN.bottle }}
          >
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold" style={{ color: SOVEREIGN.bottle }}>
              Pick an EPC role tier on Step 2 to auto-grant project permissions.
            </div>
            <p
              className="mt-1 text-[12.5px] leading-snug"
              style={{ color: SOVEREIGN.bottle, opacity: 0.8 }}
            >
              Once you choose &quot;Acts as&quot; (PMO, Project Head, Dept Head,
              Manager, ...), this card will list the project permissions that
              tier gets automatically. You can still override them below.
            </p>
            {onJumpToDepartmentStep && (
              <button
                type="button"
                onClick={onJumpToDepartmentStep}
                className="mt-3 inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-white transition-colors"
                style={{ background: SOVEREIGN.btn }}
              >
                Go to Step 2 · Department
              </button>
            )}
          </div>
        </div>
      </section>
    );
  }

  const autoGrants = TIER_AUTO_GRANTS[tag] ?? [];
  const tierLabel = TIER_LABELS[tag] ?? tag;

  return (
    <section
      className="rounded-2xl overflow-hidden"
      style={{
        border: `2px solid ${SOVEREIGN.bottle}`,
        background: SOVEREIGN.cardBg,
      }}
    >
      <header
        className="flex items-center justify-between gap-3 px-5 py-4"
        style={{ borderBottom: `1px solid ${SOVEREIGN.innerRule}` }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white"
            style={{ background: SOVEREIGN.bottle }}
          >
            <Sparkles className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div
              className="text-[11px] font-semibold uppercase tracking-wider"
              style={{ color: SOVEREIGN.brand }}
            >
              EPC Projects · auto-derived
            </div>
            <div
              className="text-sm font-bold truncate"
              style={{ color: SOVEREIGN.title }}
            >
              Acts as: {tierLabel}
              <span
                className="ml-1 text-[11px] font-medium"
                style={{ color: SOVEREIGN.kpiCaption }}
              >
                · derived from Step 2
              </span>
            </div>
          </div>
        </div>
        {onJumpToDepartmentStep && (
          <button
            type="button"
            onClick={onJumpToDepartmentStep}
            className="shrink-0 rounded-lg px-3 py-1.5 text-[11.5px] font-semibold transition-colors"
            style={{
              border: `1px solid ${SOVEREIGN.cardBorder}`,
              background: SOVEREIGN.inputBg,
              color: SOVEREIGN.bottle,
            }}
          >
            Change
          </button>
        )}
      </header>

      <div className="px-5 py-4">
        <p
          className="text-[12px] mb-3"
          style={{ color: SOVEREIGN.kpiCaption }}
        >
          The actions below were granted automatically for this tier:
        </p>
        <div className="flex flex-wrap gap-2">
          {autoGrants.length === 0 ? (
            <span
              className="text-[12px] italic"
              style={{ color: SOVEREIGN.kpiCaption }}
            >
              No permissions are auto-granted for this tier.
            </span>
          ) : (
            autoGrants.map((g) => (
              <span
                key={g.action}
                className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11.5px] font-semibold"
                style={{
                  background: 'rgba(201,169,97,0.14)',
                  border: `1px solid ${SOVEREIGN.champagne}`,
                  color: SOVEREIGN.bottle,
                }}
              >
                <span
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: SOVEREIGN.champagne }}
                />
                {g.label}
              </span>
            ))
          )}
        </div>
      </div>

      <details
        className="px-5 py-3.5 text-[12px]"
        style={{
          borderTop: `1px solid ${SOVEREIGN.innerRule}`,
          background: SOVEREIGN.paper,
        }}
      >
        <summary
          className="flex items-center gap-1.5 select-none cursor-pointer font-semibold"
          style={{ color: SOVEREIGN.bottle }}
        >
          <ChevronDown className="h-3.5 w-3.5" />
          Advanced — override permissions manually
        </summary>
        <p
          className="mt-2 text-[11.5px] mb-3"
          style={{ color: SOVEREIGN.kpiCaption }}
        >
          For power admins. Toggle any action below to grant extras or remove
          auto-granted defaults. Changing the tier on Step 2 will reset these
          back to the tier defaults.
        </p>

        <div className="mb-3">
          <div
            className="text-[10.5px] font-bold uppercase tracking-wider mb-1.5"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            CRUD actions
          </div>
          <div className="flex flex-wrap gap-3">
            {CRUD_ACTIONS.map((a) => (
              <label
                key={a.key}
                className="inline-flex items-center gap-1.5 text-[12px]"
                style={{ color: SOVEREIGN.ink }}
              >
                <input
                  type="checkbox"
                  checked={granted.has(a.key)}
                  onChange={(e) => onTogglePermission(a.key, e.target.checked)}
                  className="h-4 w-4 rounded"
                  style={{ accentColor: SOVEREIGN.bottle } as CSSProperties}
                />
                {a.label}
              </label>
            ))}
          </div>
        </div>

        <div>
          <div
            className="text-[10.5px] font-bold uppercase tracking-wider mb-1.5"
            style={{ color: SOVEREIGN.kpiCaption }}
          >
            Workspace grants
          </div>
          <div className="flex flex-wrap gap-3">
            {TIER_ACTIONS.map((a) => (
              <label
                key={a.key}
                className="inline-flex items-center gap-1.5 text-[12px]"
                style={{ color: SOVEREIGN.ink }}
              >
                <input
                  type="checkbox"
                  checked={granted.has(a.key)}
                  onChange={(e) => onTogglePermission(a.key, e.target.checked)}
                  className="h-4 w-4 rounded"
                  style={{ accentColor: SOVEREIGN.bottle } as CSSProperties}
                />
                {a.label}
              </label>
            ))}
          </div>
        </div>
      </details>
    </section>
  );
}

const EpcProjectsTierAutoGrant = memo(EpcProjectsTierAutoGrantImpl);
export default EpcProjectsTierAutoGrant;
